import { useApolloClient } from '@apollo/client';
import { useCallback, useState } from 'react';

import { GET_SINGLE_TENANT_REDIRECT_URL } from '@neo.tax/shared/graphql/documents.graphql';
import {
  GetSingleTenantRedirectUrlQuery,
  GetSingleTenantRedirectUrlQueryVariables,
} from '@neo.tax/shared/graphql/types';
import { toHttpUrl } from '@neo.tax/shared/utils/url.utils';

/**
 * How long the interstitial shows before the browser navigates, so the user can read why they're
 * being sent elsewhere.
 */
const REDIRECT_DELAY_MS = 3000;

type RedirectOptions = {
  /**
   * Forward the typed email to the target instance's login as a prefill. Omitted for SSO/OAuth,
   * where no email is typed up front.
   */
  forwardEmail?: boolean;
};

type UseDomainRedirect = {
  isRedirecting: boolean;
  /**
   * For the email-typed forms: looks up the domain and, if mapped, shows the interstitial + redirects.
   * Returns true when a redirect was triggered (caller stops its own auth flow), false otherwise.
   */
  redirectIfSingleTenant: (email: string, options?: RedirectOptions) => Promise<boolean>;
  /**
   * For the SSO/OAuth flow, which only learns the email after the popup: the raw Apollo lookup,
   * injected into `startOAuth` so the Firebase util stays query-free. Returns the instance URL or null.
   */
  getRedirectUrlForEmail: (email: string) => Promise<string | null>;
};

/**
 * Single-tenant domain redirect at the login layer. Looks up whether an email's domain belongs to a
 * single-tenant Neo.Tax instance (via Apollo) and routes the user there. A lookup failure never
 * blocks login — it resolves to "no redirect".
 */
export const useDomainRedirect = (): UseDomainRedirect => {
  const client = useApolloClient();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const getRedirectUrlForEmail = useCallback(
    async (email: string): Promise<string | null> => {
      const trimmedEmail = email.trim().toLowerCase();
      if (!trimmedEmail) {
        return null;
      }
      try {
        const { data } = await client.query<GetSingleTenantRedirectUrlQuery, GetSingleTenantRedirectUrlQueryVariables>({
          query: GET_SINGLE_TENANT_REDIRECT_URL,
          variables: { email: trimmedEmail },
          fetchPolicy: 'network-only',
        });
        return data.getSingleTenantRedirectUrl ?? null;
      } catch {
        return null;
      }
    },
    [client],
  );

  const redirectIfSingleTenant = async (email: string, options?: RedirectOptions): Promise<boolean> => {
    const redirectUrl = await getRedirectUrlForEmail(email);
    if (!redirectUrl) {
      return false;
    }

    // Resolve + protocol-guard the target before navigating: assigning an unvalidated redirect URL
    // to window.location.href is an XSS sink (a javascript:/data: value would execute).
    const target = toHttpUrl('/login', redirectUrl);
    if (!target) {
      return false;
    }

    setIsRedirecting(true);
    if (options?.forwardEmail) {
      target.searchParams.set('prefillEmail', email.trim().toLowerCase());
    }
    window.setTimeout(() => {
      window.location.href = target.toString();
    }, REDIRECT_DELAY_MS);
    return true;
  };

  return { isRedirecting, redirectIfSingleTenant, getRedirectUrlForEmail };
};
